import { styled } from '@fluentui/react';
import * as React from 'react';
import { Suspense, useState, useEffect } from 'react';
import { appContext } from '../../../../web/models/getHelp/appContext';
import { EmeraldUserType } from '../../../../web/models/getHelp/emeraldUserType';
import { isGetHelpSourceApp } from '../../../../web/utils/statelessUtil';
import { SourceApps } from '../../../models/constants/constants';
import { IUITabViewInfo, UITabView } from '../../../models/reduxState';
import { RequestCreationTabViewContainer } from '../containerComponents/RequestCreationTabViewContainer';
import { getContactUsClassNames, getContactUsStyles, IContactUsStyles } from '../styles/contactUsStyles';
import { SAOsUpdateContactSupportCard } from '../../../../web/self-help/components/SAOsUpdateContactSupportCard';
import { SAOsUpdateSearchCard } from '../../../../web/self-help/components/SAOsUpdateSearchCard';
import { SAEndOfLifeSearchCard } from '../../../../web/self-help/components/SAEndOfLifeSearchCard';
import { SAEndOfLifeContactSupportCard } from '../../../../web/self-help/components/SAEndOfLifeContactSupportCard';
import { IStyledComponent } from '../styles/themesUI';
import { inEmsSession } from '../../../../common/utils/commonUtilities';
import { Win10EndOfSupportCard } from '../../../../web/self-help/components/Win10EndOfSupportCard';

/* eslint-disable promise/prefer-await-to-then */

const FormQuerySubmitContainer = React.lazy(() =>
  import('../containerComponents/FormQuerySubmitContainer').then(module => ({
    default: module.FormQuerySubmitContainer,
  }))
);
const AnnouncementFloat = React.lazy(() =>
  import('../../../../web/common/components/AnnouncementFloat').then(module => ({ default: module.AnnouncementFloat }))
);
const FormHeaderContainer = React.lazy(() =>
  import('../containerComponents/FormHeaderContainer').then(module => ({
    default: module.FormHeaderContainer,
  }))
);
const FormNoticeListContainer = React.lazy(() =>
  import('../containerComponents/FormNoticeListContainer').then(module => ({
    default: module.FormNoticeListContainer,
  }))
);
const GetHelpCommercialFooter = React.lazy(() =>
  import('../../../../web/common/components/GetHelpCommercialFooter').then(module => ({
    default: module.GetHelpCommercialFooter,
  }))
);
const RequestHistoryContainer = React.lazy(() =>
  import('../containerComponents/RequestHistory/RequestHistoryContainer').then(module => ({
    default: module.RequestHistoryContainer,
  }))
);
const RequestDetailsContainer = React.lazy(() =>
  import('../containerComponents/RequestDetails/RequestDetailsContainer').then(module => ({
    default: module.RequestDetailsContainer,
  }))
);
const SelfHelpAlchemyContainer = React.lazy(() =>
  import('../containerComponents/SelfHelpAlchemyContainer').then(module => ({
    default: module.SelfHelpAlchemyContainer,
  }))
);
const ServiceRequestPageManager = React.lazy(() =>
  import('../../../../web/common/components/ServiceRequestPageManager').then(module => ({
    default: module.ServiceRequestPageManager,
  }))
);
const BackButton = React.lazy(() =>
  import('../../../../web/common/components/BackButton').then(module => ({
    default: module.BackButton,
  }))
);

export interface ContactUsBodyProps {
  sourceApp: string;
  isAgave: boolean;
  hideSelfHelpSmcIvr: boolean;
  isOutlookDotCom: boolean;
  uITabViewStack: IUITabViewInfo[];
  showModalitiesStickyFooter: boolean;
  isThirdParty: boolean;
  isAmc: boolean;
  useAnnouncementFloat: boolean;
  shouldResetFocus: boolean;
  isSmbHub: boolean;
  showWindowsUpdateAvailableForSASelfHelp: boolean;
  showWindowsUpdateAvailableForSAModalities: boolean;
  showEndOfLifeMessageForSASelfHelp: boolean;
  showEndOfLifeMessageForSAModalities: boolean;
  showWin10EndOfSupportMessage: boolean;
  resetContactUsFocus: (value: boolean) => void;
}

type IContactUsBodyProps = ContactUsBodyProps & IStyledComponent<IContactUsStyles>;

const ContactUsBody = (props: IContactUsBodyProps) => {
  const containerRef = React.createRef<HTMLDivElement>();
  const [backButtonRendered, setBackButtonRendered] = useState(false);
  let searchOutdatedDevicesAlert = props.showWindowsUpdateAvailableForSASelfHelp ? <SAOsUpdateSearchCard theme={props.theme} /> : null;
  let contactSupportOutdatedDevicesAlert = props.showWindowsUpdateAvailableForSAModalities ? <SAOsUpdateContactSupportCard theme={props.theme} /> : null;
  let searchEndOfLifeDevicesAlert = props.showEndOfLifeMessageForSASelfHelp ? <SAEndOfLifeSearchCard theme={props.theme} /> : null;
  let contactSupportEndOfLifeDevicesAlert = props.showEndOfLifeMessageForSAModalities ? <SAEndOfLifeContactSupportCard theme={props.theme} /> : null;
  let win10EndOfSupportAlert = props.showWin10EndOfSupportMessage ? <Win10EndOfSupportCard theme={props.theme} /> : null;

  // Function to handle BackButton rendering completion
  const handleBackButtonRendered = (isVisible: boolean) => {
    setBackButtonRendered(isVisible);
  };

  const getNextFocusableElement = (): HTMLElement | null => {
    if (!containerRef.current) {
      return null;
    }

    const elements = containerRef.current.querySelectorAll('button');
    const focusable = elements.length > 0 ? elements[0] : null;

    return focusable as HTMLElement;
  };

  useEffect(() => {
      if (props.shouldResetFocus && backButtonRendered) {
      const nextFocusableElement = getNextFocusableElement();
      if (nextFocusableElement) {
        nextFocusableElement.focus();
        props.resetContactUsFocus(false);
      }
    }
  }, [props.shouldResetFocus, backButtonRendered]);

  let body = null;
  let appBarBackEnabled = false;
  const contactUsClassNames = getContactUsClassNames(props.styles, { theme: props.theme, sourceApp: props.sourceApp, isAgave: props.isAgave });
  if (props.uITabViewStack && props.uITabViewStack.length > 0) {
    const currentUITabView = props.uITabViewStack[props.uITabViewStack.length - 1];
    appBarBackEnabled = currentUITabView.showAppBarBackButton;
    switch (currentUITabView.uITabView) {
      case UITabView.searchHelp:
        const showServiceRequestManager = (props.isOutlookDotCom && inEmsSession()) ||
          (!props.showModalitiesStickyFooter && !props.isThirdParty);
        body = (
          <div id="contact-us-body-id" className={contactUsClassNames.contactUsBody}>
            {props.useAnnouncementFloat ? <AnnouncementFloat /> : <FormNoticeListContainer />}
            {
              !(props.isOutlookDotCom || props.isAmc || props.isSmbHub) &&
              (
                <>
                  {searchOutdatedDevicesAlert}
                  {searchEndOfLifeDevicesAlert}
                  {win10EndOfSupportAlert}
                  <FormHeaderContainer />
                  <FormQuerySubmitContainer />
                  {
                    !(props.hideSelfHelpSmcIvr && (props.sourceApp === SourceApps.SMCIVR || props.sourceApp === SourceApps.SMCIVR2)) &&
                    <SelfHelpAlchemyContainer />
                  }
                  {isGetHelpSourceApp(props.sourceApp) && appContext.emeraldUserType === EmeraldUserType.Commercial && <GetHelpCommercialFooter />}
                </>
              )
            }
            {showServiceRequestManager && <ServiceRequestPageManager theme={props.theme} />}
          </div>
        );
        break;
      case UITabView.requestCreation:
        body = (
          <div>
            {contactSupportOutdatedDevicesAlert}
            {contactSupportEndOfLifeDevicesAlert}
            <RequestCreationTabViewContainer />
          </div>
        );
        break;
      case UITabView.requestHistory:
        body = <RequestHistoryContainer />;
        break;
      case UITabView.requestDetails:
        body = <RequestDetailsContainer />;
        break;
      default:
        body = null;
        break;
    }
  }
  return (
    <div ref={containerRef} className={contactUsClassNames.outlineNone}>
      <Suspense fallback={null}>
        {!appBarBackEnabled && <BackButton theme={props.theme} onRenderBackButtonComplete={handleBackButtonRendered}/>}
        {body}
      </Suspense>
    </div>
  );
};

export default styled(ContactUsBody, getContactUsStyles);
